import { TableSortingDirection, Table } from 'app/components/shared/Table'
import { Text } from 'app/components/ui/Text'
import { protectedRoutes } from 'app/routing/routes'
import { formatDateShort } from 'app/utils/dateUtils'
import {
  sponsorRequestStatusColor,
  sponsorRequestStatusDictionary,
} from 'app/utils/sponsorRequest'
import { SponsorRequest, SponsorRequestStatus } from 'EstraSponsor'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { SponsorRequestsTableHeader } from './SponsorRequestTableHeader'
import {
  getFilteredRequests,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_KEY,
  tableHeadings,
  TableHeadingsKeys,
} from './utils'

type Props = {
  sponsorRequests: SponsorRequest[]
  onAddSponsorRequest: () => void
}

export const SponsorRequestsTable = ({
  sponsorRequests,
  onAddSponsorRequest,
}: Props): JSX.Element => {
  const { push } = useHistory()
  const theme = useTheme()

  const [sortedRequests, setSortedRequests] = useState<SponsorRequest[]>([])

  const [activeSortDirection, setActiveSortDirection] =
    useState<TableSortingDirection>(INITIAL_SORT_DIRECTION)

  const [activeSortKey, setActiveSortKey] =
    useState<TableHeadingsKeys>(INITIAL_SORT_KEY)

  const sortList = (
    list: SponsorRequest[],
    sortKey: TableHeadingsKeys,
    sortDirection: TableSortingDirection
  ) => {
    const newList = [...list].sort((a, b) => {
      const aSort = a[sortKey]
      const bSort = b[sortKey]
      if (!aSort || !bSort) return -1
      return aSort > bSort ? -1 : 1
    })
    setActiveSortDirection(sortDirection)
    setActiveSortKey(sortKey)
    setSortedRequests(sortDirection === 'DESC' ? newList : newList.reverse())
  }

  // Initial use effect, sort list for the first render
  useEffect(() => {
    sortList(
      sponsorRequests,
      INITIAL_SORT_KEY as TableHeadingsKeys,
      INITIAL_SORT_DIRECTION
    )
  }, [sponsorRequests])

  return (
    <>
      <SponsorRequestsTableHeader
        requestsCount={sponsorRequests.length}
        onAddSponsorRequest={onAddSponsorRequest}
        onFilterChanged={(filter) => {
          const filteredRequests = getFilteredRequests(sponsorRequests, filter)
          sortList(
            filteredRequests,
            activeSortKey as TableHeadingsKeys,
            activeSortDirection
          )
        }}
      ></SponsorRequestsTableHeader>
      <Table<TableHeadingsKeys>
        headings={tableHeadings}
        sortKey={activeSortKey}
        sortDirection={activeSortDirection}
        clickableRows
        hover
        onSorting={(key, direction) => {
          sortList(sortedRequests, key as TableHeadingsKeys, direction)
        }}
      >
        <tbody>
          {sortedRequests.map((item) => (
            <tr
              key={item.id}
              onClick={() => {
                push(protectedRoutes.sponsorRequestShow.makeUrl(item.id))
              }}
            >
              <td>{item.sent_at ? formatDateShort(item.sent_at) : '-'}</td>
              <td>
                <Text weight='700'>{item.name}</Text>
              </td>

              <td>
                {
                  <Text
                    color={
                      theme.color.primari[
                        sponsorRequestStatusColor[
                          item.status as SponsorRequestStatus
                        ]
                      ]
                    }
                  >
                    {
                      sponsorRequestStatusDictionary[
                        item.status as SponsorRequestStatus
                      ]
                    }
                    {item.status === 'draft' && item.ready_to_be_sent
                      ? ' (Da inviare)'
                      : ''}
                  </Text>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  )
}
